import { RowCrudStatus } from "../../common/types";
import {
  DesignShowcase,
  DesignShowcaseCreateMutationVariables,
  DesignShowcaseUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { blankToNull } from "../../../common/blankToNull";
import { ShowcaseStatus } from "../../../api/graphql";
import { showcaseCategoryDisplayId } from "../../showcaseCategory/store/ShowcaseCategoryCommonStore";
import { showcaseWorkflowStateDisplayId } from "../../showcaseWorkflowState/store/ShowcaseWorkflowStateCommonStore";
import { diaDeCalendario } from "../../../common/calendarDate";
import { FileCRUDType, fileCRUDInit } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { getFileVariableValue } from "../../common/utils";
import { safeJsonParse } from "../../../common/utils";
import {
  ShowcaseLineValuesType,
  getShowcaseLineValues,
  getShowcaseLineCreateInput,
  getShowcaseLineUpdateInput,
} from "../../showcaseLine/store/ShowcaseLineCommonStore";
import {
  addedStatusList,
  originalDeletedStatusList,
  originalUpdatedStatusList,
} from "../../common/types";

let uniqueCounter = 1;
export type DesignShowcaseValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  code: string;
  name: string;
  summary: string;
  status: ShowcaseStatus;
  __category: string;
  showCategory: { displayId: string; id: string } | null;
  __relatedCategories: string[];
  showRelatedCategories: { displayId: string; id: string }[];
  appliedStatuses: ShowcaseStatus[];
  lifecycle_transitions_: string[];
  __lifecycle: string;
  showLifecycle: { displayId: string; id: string } | null;
  quantity: number;
  durationInYears: number | undefined;
  amount: number;
  ratio: number | undefined;
  startDate: string | null;
  expiresAt: Date | null;
  registeredAt: Date | null;
  active: boolean;
  highlighted: boolean;
  phone: string;
  taxId: string;
  attachment: string;
  attachment_data: FileCRUDType;
  __attachment_files: FileCRUDType[];
  anyFile: string;
  anyFile_data: FileCRUDType;
  __anyFile_files: FileCRUDType[];
  avatar: string;
  avatar_data: FileCRUDType;
  __avatar_files: FileCRUDType[];
  keywords: string[];
  body: string;
  metadata: string;
  internalNote: string;
  apiKey: string;
  apiKeyHasValue: boolean;
  serverLabel: string;
  showLines: ShowcaseLineValuesType[];
};

export type DesignShowcaseErrorsType = {
  code?: { message: string; fieldLabel: string };
  name?: { message: string; fieldLabel: string };
  summary?: { message: string; fieldLabel: string };
  status?: { message: string; fieldLabel: string };
  category?: { message: string; fieldLabel: string };
  relatedCategories?: { message: string; fieldLabel: string };
  appliedStatuses?: { message: string; fieldLabel: string };
  lifecycle?: { message: string; fieldLabel: string };
  quantity?: { message: string; fieldLabel: string };
  durationInYears?: { message: string; fieldLabel: string };
  amount?: { message: string; fieldLabel: string };
  ratio?: { message: string; fieldLabel: string };
  startDate?: { message: string; fieldLabel: string };
  expiresAt?: { message: string; fieldLabel: string };
  registeredAt?: { message: string; fieldLabel: string };
  active?: { message: string; fieldLabel: string };
  highlighted?: { message: string; fieldLabel: string };
  phone?: { message: string; fieldLabel: string };
  taxId?: { message: string; fieldLabel: string };
  attachment?: { message: string; fieldLabel: string };
  anyFile?: { message: string; fieldLabel: string };
  avatar?: { message: string; fieldLabel: string };
  keywords?: { message: string; fieldLabel: string };
  body?: { message: string; fieldLabel: string };
  metadata?: { message: string; fieldLabel: string };
  internalNote?: { message: string; fieldLabel: string };
  apiKey?: { message: string; fieldLabel: string };
  serverLabel?: { message: string; fieldLabel: string };
  lines?: { message: string; fieldLabel: string };
};

export function DesignShowcaseValuesInit(): DesignShowcaseValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    code: "",
    name: "",
    summary: "",
    status: "draft" as ShowcaseStatus,
    __category: "",
    showCategory: null,
    __relatedCategories: [],
    showRelatedCategories: [],
    appliedStatuses: [],
    lifecycle_transitions_: [],
    __lifecycle: "",
    showLifecycle: null,
    quantity: 0,
    durationInYears: undefined,
    amount: 0,
    ratio: undefined,
    startDate: null,
    expiresAt: null,
    registeredAt: null,
    active: false,
    highlighted: false,
    phone: "",
    taxId: "",
    attachment: "",
    attachment_data: fileCRUDInit(),
    __attachment_files: [],
    anyFile: "",
    anyFile_data: fileCRUDInit(),
    __anyFile_files: [],
    avatar: "",
    avatar_data: fileCRUDInit(),
    __avatar_files: [],
    keywords: [],
    body: "",
    metadata: "{}",
    internalNote: "",
    apiKey: "",
    apiKeyHasValue: false,
    serverLabel: "",
    showLines: [],
  };
}

export type DesignShowcaseType = DesignShowcase | DesignShowcaseValuesType;

export function designShowcaseDisplayId(
  source: Partial<DesignShowcase>,
  _langStore: LangStore
) {
  return source.code ?? _langStore.emptyStringSymbol;
}

export function getDesignShowcaseValues(
  source: DesignShowcaseType,
  _langStore: LangStore,
  _duplicated: boolean = false
): DesignShowcaseValuesType {
  const entityValues = DesignShowcaseValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : designShowcaseDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.code = source.code;
  entityValues.name = source.name;
  entityValues.summary = source.summary || "";
  entityValues.status = source.status;
  entityValues.showCategory =
    "category" in source && source.category
      ? {
          id: source.category.id,
          displayId: showcaseCategoryDisplayId(source.category, _langStore),
        }
      : "showCategory" in source && source.showCategory
      ? { id: source.showCategory.id, displayId: source.showCategory.displayId }
      : null;
  entityValues.__category = entityValues.showCategory?.displayId || "";
  entityValues.showRelatedCategories =
    "relatedCategories" in source && source.relatedCategories
      ? (source.relatedCategories || []).map((x) => {
          return {
            id: x.id,
            displayId: showcaseCategoryDisplayId(x, _langStore),
          };
        })
      : "showRelatedCategories" in source && source.showRelatedCategories
      ? (source.showRelatedCategories || []).map((x) => {
          return { id: x.id, displayId: x.displayId };
        })
      : [];
  entityValues.__relatedCategories = entityValues.showRelatedCategories.map(
    (x) => x.displayId
  );
  entityValues.appliedStatuses = source.appliedStatuses || [];
  entityValues.lifecycle_transitions_ = source.lifecycle_transitions_;
  entityValues.showLifecycle =
    "lifecycle" in source && source.lifecycle
      ? {
          id: source.lifecycle.id,
          displayId: showcaseWorkflowStateDisplayId(
            source.lifecycle,
            _langStore
          ),
        }
      : "showLifecycle" in source && source.showLifecycle
      ? {
          id: source.showLifecycle.id,
          displayId: source.showLifecycle.displayId,
        }
      : null;
  entityValues.__lifecycle = entityValues.showLifecycle?.displayId || "";
  entityValues.quantity = source.quantity || 0;
  entityValues.durationInYears =
    (source.durationInYears as unknown) === "" ||
    source.durationInYears === undefined ||
    source.durationInYears === null
      ? undefined
      : source.durationInYears;
  entityValues.amount = Number(source.amount) || 0;
  entityValues.ratio =
    (source.ratio as unknown) === "" ||
    source.ratio === undefined ||
    source.ratio === null
      ? undefined
      : source.ratio;
  entityValues.startDate = diaDeCalendario(source.startDate);
  entityValues.expiresAt = source.expiresAt ? new Date(source.expiresAt) : null;
  entityValues.registeredAt = source.registeredAt
    ? new Date(source.registeredAt)
    : null;
  entityValues.active = source.active || false;
  entityValues.highlighted = source.highlighted || false;
  entityValues.phone = source.phone || "";
  entityValues.taxId = source.taxId || "";
  entityValues.__attachment_files =
    source.attachment_data && !_duplicated
      ? [
          {
            id:
              "id" in source.attachment_data && source.attachment_data.id
                ? source.attachment_data.id
                : `generated_${uniqueCounter++}`,
            name: source.attachment_data.name,
            description: source.attachment_data.description || undefined,
            size: source.attachment_data.size || 0,
            formattedSize: source.attachment_data.size
              ? formatBytes(source.attachment_data.size)
              : "",
            mimetype: source.attachment_data.mimetype,
            url: source.attachment_data.url || undefined,
            __status__: "original",
          },
        ]
      : [];
  entityValues.attachment =
    source.attachment && !_duplicated ? source.attachment : "";
  entityValues.attachment_data =
    source.attachment_data && "__status__" in source.attachment_data
      ? source.attachment_data
      : source.attachment_data
      ? {
          id: ("id" in source.attachment_data && source.attachment_data.id
            ? source.attachment_data.id
            : `generated_${uniqueCounter++}`) as string,
          name: source.attachment_data.name,
          description: source.attachment_data.description || undefined,
          size: source.attachment_data.size || 0,
          formattedSize: source.attachment_data.size
            ? formatBytes(source.attachment_data.size)
            : "",
          mimetype: source.attachment_data.mimetype,
          url: source.attachment_data.url || undefined,
          __status__: "original" as RowCrudStatus,
        }
      : fileCRUDInit();
  entityValues.__anyFile_files =
    source.anyFile_data && !_duplicated
      ? [
          {
            id:
              "id" in source.anyFile_data && source.anyFile_data.id
                ? source.anyFile_data.id
                : `generated_${uniqueCounter++}`,
            name: source.anyFile_data.name,
            description: source.anyFile_data.description || undefined,
            size: source.anyFile_data.size || 0,
            formattedSize: source.anyFile_data.size
              ? formatBytes(source.anyFile_data.size)
              : "",
            mimetype: source.anyFile_data.mimetype,
            url: source.anyFile_data.url || undefined,
            __status__: "original",
          },
        ]
      : [];
  entityValues.anyFile = source.anyFile && !_duplicated ? source.anyFile : "";
  entityValues.anyFile_data =
    source.anyFile_data && "__status__" in source.anyFile_data
      ? source.anyFile_data
      : source.anyFile_data
      ? {
          id: ("id" in source.anyFile_data && source.anyFile_data.id
            ? source.anyFile_data.id
            : `generated_${uniqueCounter++}`) as string,
          name: source.anyFile_data.name,
          description: source.anyFile_data.description || undefined,
          size: source.anyFile_data.size || 0,
          formattedSize: source.anyFile_data.size
            ? formatBytes(source.anyFile_data.size)
            : "",
          mimetype: source.anyFile_data.mimetype,
          url: source.anyFile_data.url || undefined,
          __status__: "original" as RowCrudStatus,
        }
      : fileCRUDInit();
  entityValues.__avatar_files =
    source.avatar_data && !_duplicated
      ? [
          {
            id:
              "id" in source.avatar_data && source.avatar_data.id
                ? source.avatar_data.id
                : `generated_${uniqueCounter++}`,
            name: source.avatar_data.name,
            description: source.avatar_data.description || undefined,
            size: source.avatar_data.size || 0,
            formattedSize: source.avatar_data.size
              ? formatBytes(source.avatar_data.size)
              : "",
            mimetype: source.avatar_data.mimetype,
            url: source.avatar_data.url || undefined,
            __status__: "original",
          },
        ]
      : [];
  entityValues.avatar = source.avatar && !_duplicated ? source.avatar : "";
  entityValues.avatar_data =
    source.avatar_data && "__status__" in source.avatar_data
      ? source.avatar_data
      : source.avatar_data
      ? {
          id: ("id" in source.avatar_data && source.avatar_data.id
            ? source.avatar_data.id
            : `generated_${uniqueCounter++}`) as string,
          name: source.avatar_data.name,
          description: source.avatar_data.description || undefined,
          size: source.avatar_data.size || 0,
          formattedSize: source.avatar_data.size
            ? formatBytes(source.avatar_data.size)
            : "",
          mimetype: source.avatar_data.mimetype,
          url: source.avatar_data.url || undefined,
          __status__: "original" as RowCrudStatus,
        }
      : fileCRUDInit();
  entityValues.keywords = source.keywords || [];
  entityValues.body = source.body || "";
  entityValues.metadata = source.metadata || "";
  entityValues.internalNote = source.internalNote || "";
  entityValues.apiKey = "";
  entityValues.apiKeyHasValue = !!source.apiKeyHasValue;
  entityValues.serverLabel = source.serverLabel || "";
  entityValues.showLines =
    "lines" in source && source.lines
      ? (source.lines || []).map((x) => {
          const row: ShowcaseLineValuesType = getShowcaseLineValues(
            x,
            _langStore,
            _duplicated
          );
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showLines" in source && source.showLines
      ? (source.showLines || []).map((x) => {
          const row: ShowcaseLineValuesType = getShowcaseLineValues(
            x,
            _langStore,
            _duplicated
          );
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getDesignShowcaseUpdateVariables(
  values: DesignShowcaseValuesType
): DesignShowcaseUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    code: values.code,
    name: values.name,
    summary: blankToNull(values.summary),
    status: values.status,
    categoryId: values.showCategory?.id || "",
    relatedCategoriesIds: values.showRelatedCategories
      ? { set: values.showRelatedCategories.map((x) => x.id) }
      : undefined,
    appliedStatuses: values.appliedStatuses,
    quantity: values.quantity,
    durationInYears:
      values.durationInYears === undefined ? null : values.durationInYears,
    amount: values.amount,
    ratio: values.ratio === undefined ? null : values.ratio,
    startDate: values.startDate!,
    expiresAt: values.expiresAt?.toISOString() || null,
    registeredAt: values.registeredAt?.toISOString() || null,
    active: values.active,
    highlighted: values.highlighted,
    phone: blankToNull(values.phone),
    taxId: blankToNull(values.taxId),
    attachment: getFileVariableValue(values.__attachment_files),
    anyFile: getFileVariableValue(values.__anyFile_files),
    avatar: getFileVariableValue(values.__avatar_files),
    keywords: values.keywords || null,
    body: blankToNull(values.body),
    metadata: safeJsonParse(values.metadata, "metadata"),
    internalNote: blankToNull(values.internalNote),
    apiKey: values.apiKey || undefined,
    lines: {
      create: values.showLines
        .filter((x) => addedStatusList.includes(x.__status__))
        .map((x) => getShowcaseLineCreateInput(x)),
      update: values.showLines
        .filter((x) => originalUpdatedStatusList.includes(x.__status__))
        .map((x) => getShowcaseLineUpdateInput(x)),
      remove: values.showLines
        .filter((x) => originalDeletedStatusList.includes(x.__status__))
        .map((x) => x.id),
    },
  };
}

export function getDesignShowcaseCreateVariables(
  values: DesignShowcaseValuesType
): DesignShowcaseCreateMutationVariables {
  return {
    code: values.code,
    name: values.name,
    summary: blankToNull(values.summary),
    status: values.status,
    categoryId: values.showCategory?.id || "",
    relatedCategoriesIds: values.showRelatedCategories
      ? values.showRelatedCategories.map((x) => x.id)
      : undefined,
    appliedStatuses: values.appliedStatuses,
    quantity: values.quantity,
    durationInYears:
      values.durationInYears === undefined ? null : values.durationInYears,
    amount: values.amount,
    ratio: values.ratio === undefined ? null : values.ratio,
    startDate: values.startDate!,
    expiresAt: values.expiresAt?.toISOString() || null,
    registeredAt: values.registeredAt?.toISOString() || null,
    active: values.active,
    highlighted: values.highlighted,
    phone: blankToNull(values.phone),
    taxId: blankToNull(values.taxId),
    attachment: getFileVariableValue(values.__attachment_files),
    anyFile: getFileVariableValue(values.__anyFile_files),
    avatar: getFileVariableValue(values.__avatar_files),
    keywords: values.keywords || null,
    body: blankToNull(values.body),
    metadata: safeJsonParse(values.metadata, "metadata"),
    internalNote: blankToNull(values.internalNote),
    apiKey: values.apiKey || undefined,
    lines: values.showLines
      .filter((x) => addedStatusList.includes(x.__status__))
      .map((x) => getShowcaseLineCreateInput(x)),
  };
}
